import { useEffect, useRef, useState } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { RootState } from 'store'
import useAuth from 'hooks/useAuth'
import { setSidebarOpen } from 'features/global/globalSlice'
import { NavItem } from 'components/ui/NavItem/NavItem'
import { SidebarBrandLogo } from './SidebarBrandLogo'
import { linksClient, linksAdmin, ILink } from 'constants/links'
import { filterLinksByPermission, isModuleActive, isPathActive, normalizePath } from './navUtils'
import {
  SidebarRoot,
  Brand,
  NavScroll,
  NavGroup,
  NavModule,
  SubNav,
  SubNavItem,
  SubNavCollapse,
  SubNavInner,
  Flyout,
  FlyoutTitle,
  CollapseToggle,
} from './AppSidebar.styled'

const STORAGE_KEY = 'aimSidebarExpanded'

export const AppSidebar = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isAdmin, user } = useAuth()
  const { isSidebarOpen } = useSelector((store: RootState) => store.globals)
  const collapsed = !isSidebarOpen
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false)
  const [flyout, setFlyout] = useState<{ module: ILink; top: number; left: number; maxHeight: number } | null>(null)
  const [expandedIds, setExpandedIds] = useState<number[]>([])
  const sidebarRef = useRef<HTMLElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const openFlyout = (module: ILink, event: React.MouseEvent<HTMLElement>) => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    const moduleRect = event.currentTarget.getBoundingClientRect()
    const sidebarRight = sidebarRef.current?.getBoundingClientRect().right ?? moduleRect.right
    setFlyout({
      module,
      top: moduleRect.top,
      left: sidebarRight + 6,
      maxHeight: window.innerHeight - moduleRect.top - 16,
    })
  }

  const scheduleCloseFlyout = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setFlyout(null), 140)
  }

  const cancelCloseFlyout = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }

  const goToSub = (path: string) => {
    navigate(normalizePath(path))
    setFlyout(null)
  }

  useEffect(() => {
    let mounted = true
    fetchAuthSession()
      .then((session) => {
        const groups = session.tokens?.idToken?.payload['cognito:groups'] as string[] | undefined
        if (mounted && groups?.includes('company_administrator')) {
          setIsCompanyAdmin(true)
        }
      })
      .catch(() => undefined)
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    const expanded = saved === null ? true : saved === 'true'
    dispatch(setSidebarOpen(expanded))
  }, [dispatch])

  const toggleCollapsed = () => {
    const nextExpanded = collapsed
    localStorage.setItem(STORAGE_KEY, String(nextExpanded))
    dispatch(setSidebarOpen(nextExpanded))
  }

  const modules = isAdmin
    ? linksAdmin
    : isCompanyAdmin
      ? linksClient
      : filterLinksByPermission(
          linksClient,
          user?.currentContract?.role?.permissionPolicy,
        )

  return (
    <SidebarRoot ref={sidebarRef} $collapsed={collapsed}>
      <Brand $collapsed={collapsed}>
        <SidebarBrandLogo collapsed={collapsed} />
      </Brand>

      <NavScroll onMouseLeave={() => setExpandedIds([])}>
        <NavGroup>
          {modules.map((module) => {
            const moduleActive = isModuleActive(module, pathname)
            const hasSubs = !!module.list && module.list.length > 0
            const subOpen = moduleActive || expandedIds.includes(module.id)
            return (
              <NavModule
                key={module.id}
                onMouseEnter={(e) => {
                  if (collapsed) {
                    if (hasSubs) openFlyout(module, e)
                  } else if (hasSubs) {
                    setExpandedIds((prev) => (prev.includes(module.id) ? prev : [...prev, module.id]))
                  }
                }}
                onMouseLeave={collapsed && hasSubs ? scheduleCloseFlyout : undefined}
              >
                <NavItem
                  label={module.text}
                  icon={module.icon}
                  active={moduleActive}
                  collapsed={collapsed}
                  onSelect={() => navigate(normalizePath(module.path))}
                />
                {!collapsed && hasSubs && (
                  <SubNavCollapse $open={subOpen}>
                    <SubNavInner $open={subOpen}>
                      <SubNav>
                        {module.list?.map((sub) => (
                          <SubNavItem
                            key={sub.path}
                            $active={isPathActive(sub.path, pathname)}
                            aria-current={isPathActive(sub.path, pathname) ? 'page' : undefined}
                            tabIndex={subOpen ? 0 : -1}
                            onClick={() => navigate(normalizePath(sub.path))}
                          >
                            {sub.name}
                          </SubNavItem>
                        ))}
                      </SubNav>
                    </SubNavInner>
                  </SubNavCollapse>
                )}
              </NavModule>
            )
          })}
        </NavGroup>
      </NavScroll>

      <CollapseToggle
        $collapsed={collapsed}
        onClick={toggleCollapsed}
        aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
      >
        <svg
          width='18'
          height='18'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.8'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d={collapsed ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'} />
        </svg>
        {!collapsed && 'Collapse'}
      </CollapseToggle>

      {collapsed && flyout && (
        <Flyout
          style={{ top: flyout.top, left: flyout.left, maxHeight: flyout.maxHeight }}
          onMouseEnter={cancelCloseFlyout}
          onMouseLeave={scheduleCloseFlyout}
        >
          <FlyoutTitle>{flyout.module.text}</FlyoutTitle>
          {flyout.module.list?.map((sub) => (
            <SubNavItem
              key={sub.path}
              $active={isPathActive(sub.path, pathname)}
              aria-current={isPathActive(sub.path, pathname) ? 'page' : undefined}
              onClick={() => goToSub(sub.path)}
            >
              {sub.name}
            </SubNavItem>
          ))}
        </Flyout>
      )}
    </SidebarRoot>
  )
}
